import React from "react";
import { useNavigate, useSearchParams } from "react-router";

// Misc
import * as types from "types";
import styles from "styles/chat.module.scss";
import {
  loadLastSelectedPersonaId,
  setLastSelectedPersonaId,
} from "utilities/lastSelectedPersonaId";
import { isLocalOrSandbox } from "utilities/environment";
import { PRODUCT_NAME } from "utilities/productName";
// From components
import WelcomeMessage from "components/WelcomeMessage/WelcomeMessage";
import PersonaSelector from "components/PersonaSelector";
import IncognitoToggle from "components/IncognitoToggle";
import MessageInput from "components/MessageInput";
import UploadDropzone from "components/UploadDropzone";
import { useUploadProgress, uploadFiles } from "contexts/UploadContext";
import useGetAccessToken from "components/Login/useGetAccessToken";
// From services
import usePostThreadMutation from "services/dbApi/hooks/usePostThreadMutation";
import usePutMessagesMutation from "services/dbApi/hooks/usePutMessagesMutation";
import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import { useTaskWriteInFlight } from "hooks/tasks/useTaskWriteInFlight";
import { persistNewChat } from "./persistNewChat";

// Resolves the initial persona by looking up the last-selected id in
// localStorage and falling back to DEFAULT_PERSONA when nothing is stored or
// the stored persona has been deactivated.
function getInitialPersona(): types.Persona {
  const lastId = loadLastSelectedPersonaId();
  if (lastId === null) return types.DEFAULT_PERSONA;
  const persona = types.getPersonaById(lastId);
  return persona.active ? persona : types.DEFAULT_PERSONA;
}

export default function NewChat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createThreadMutation = usePostThreadMutation();
  const putMessagesMutation = usePutMessagesMutation();
  const { setUploadProgress, removeUploadProgress } = useUploadProgress();

  // Persona is plain client-side state here — there's no thread to attach it
  // to until the user sends. Persisted to localStorage so the choice survives
  // reloads and is read by Chat on first load of a new thread.
  const [persona, setPersonaState] =
    React.useState<types.Persona>(getInitialPersona);
  const setPersona = React.useCallback((newPersona: types.Persona) => {
    setPersonaState(newPersona);
    setLastSelectedPersonaId(newPersona.id);
  }, []);

  const [userInput, setUserInput] = React.useState("");
  const getAccessToken = useGetAccessToken();
  const personaNameQS = searchParams.get("personaName");

  // Here we save the user's first typed message so they can be preserved in the
  // event that the postThread call in initNewChat() fails.
  const [pendingFirstMessage, setPendingFirstMessage] =
    React.useState<types.UserMessage | null>(null);

  const { refetch: refetchThreads } = useThreadsQuery();
  const { refetch: refetchTasks } = useTasksQuery();
  const taskWriteInFlight = useTaskWriteInFlight();

  // Refetch threads and tasks on tab focus so the sidebar (and its task
  // completion dot) reflect work done in other tabs. NewChat runs no chat
  // completion stream, so no streaming gate is needed. Skipped while a task
  // write is in flight: a focus GET could land with pre-commit server state and
  // overwrite a just-dismissed/just-registered row (ADR 056).
  React.useEffect(() => {
    const handleFocus = () => {
      refetchThreads();
      if (taskWriteInFlight) return;
      refetchTasks();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetchThreads, refetchTasks, taskWriteInFlight]);

  // Creates a new chat thread and navigates the user to it.
  // New chat threads are created when:
  // - the user sends a message
  // - the user uploads a file
  const initNewChat = React.useCallback(
    async (
      opts: {
        message?: types.UserMessage;
        files?: File[];
        draftInput?: string;
      } = {}
    ) => {
      const newThread: types.ChatThread = {
        id: crypto.randomUUID(),
        name: types.DEFAULT_CHAT_THREAD_NAME,
        personaId: persona.id,
        timestamp: new Date().toISOString(),
      };

      setPendingFirstMessage(opts.message ?? null);

      try {
        // Create the chat thread.
        await createThreadMutation.mutateAsync(newThread);

        // Upload any dropped files.
        let fileMessages: types.FileMessage[] = [];
        if (opts.files && opts.files.length > 0) {
          fileMessages = await uploadFiles({
            files: opts.files,
            chatId: newThread.id,
            getAccessToken,
            setUploadProgress,
            removeUploadProgress,
          });
        }

        // Persist messages before navigating.
        const firstMessage = opts.message === undefined ? [] : [opts.message];
        const initialMessages = [...firstMessage, ...fileMessages];

        await putMessagesMutation.mutateAsync({
          threadId: newThread.id,
          messages: initialMessages,
        });

        navigate(`/chats/${newThread.id}`, {
          state: { draftInput: opts.draftInput },
        });
      } catch {
        // A persistence call failed. This will cause createThreadMutation.isError
        // or putMessagesMutation.isError to flip. We swallow the error in this
        // catch block to avoid a duplicate error signal.
      }
    },
    [
      createThreadMutation,
      putMessagesMutation,
      getAccessToken,
      setUploadProgress,
      removeUploadProgress,
      navigate,
      persona,
    ]
  );

  // Switch to persona passed in the URL querystring.
  React.useEffect(() => {
    if (personaNameQS) {
      const nextPersona = types.getPersonaByName(personaNameQS);
      if (nextPersona.active) {
        setPersona(nextPersona);
      }
    }
  }, [personaNameQS, setPersona]);

  const searchEnabled = !persona.isInternal;

  const jupyterHubEnabled =
    isLocalOrSandbox() &&
    (persona.jupyterHubCapable === undefined ||
      persona.jupyterHubCapable === true);

  // If creating the new chat failed, escalate to a MessagesWriteError (ADR 065).
  if (createThreadMutation.isError || putMessagesMutation.isError) {
    const thread = createThreadMutation.variables;

    if (thread) {
      let messages: types.Message[] = [];
      // First try to recover the messages we were trying to persist from the
      // PUT messages mutation. When putMessages ran, its variables hold the full
      // list — typed message and any file messages — so both recover here.
      if (putMessagesMutation.variables?.messages !== undefined) {
        messages = putMessagesMutation.variables?.messages;
      } else if (pendingFirstMessage !== null) {
        // Second, try to recover from the pendingFirstMessage state. This path
        // is only reached when postThread failed (putMessages never ran).
        // Known gap: if the user also dropped files, uploadFiles never ran
        // (it needs the thread to exist first), so nothing was uploaded — the
        // retry re-persists only the typed message and the user must re-drop
        // the files.
        messages = [pendingFirstMessage];
      }

      throw new types.MessagesWriteError({
        title: "Couldn't start your chat",
        detail: `${PRODUCT_NAME} couldn't save your new chat after several attempts. Nothing already saved is affected.`,
        retry: async () => {
          await persistNewChat(thread, messages, getAccessToken);
          window.location.assign(`/chats/${thread.id}`);
        },
      });
    }
  }

  return (
    <UploadDropzone
      disabled={createThreadMutation.isPending}
      onDrop={(files) => initNewChat({ files, draftInput: userInput })}
    >
      <main className={styles.chat}>
        {/* Header */}
        <div className={styles.chatHeader}>
          <PersonaSelector
            personas={types.getActivePersonas()}
            selectedPersona={persona}
            setSelectedPersona={setPersona}
          />
          <IncognitoToggle />
        </div>
        {/* Body */}
        <div className={styles.chatBody}>
          <WelcomeMessage persona={persona} />
        </div>
        {/* Sticky footer */}
        <div className={styles.chatFooter}>
          <MessageInput
            persona={persona}
            userInput={userInput}
            setUserInput={setUserInput}
            disabled={createThreadMutation.isPending}
            uploadDisabled={createThreadMutation.isPending}
            showExamplePrompts={true}
            onSend={(message) => initNewChat({ message })}
            onPasteFiles={(files) =>
              initNewChat({ files, draftInput: userInput })
            }
            onFileDrop={(files) =>
              initNewChat({ files, draftInput: userInput })
            }
            toolAvailability={{
              searchEnabled,
              jupyterHubEnabled,
            }}
          />
        </div>
      </main>
    </UploadDropzone>
  );
}
